import { NetworkStatus } from '@apollo/client';
import { Box, Spinner, SpinnerSize, Text, ToastAction, ToastType, useToastsDispatch } from '@bechtel/gds';
import { Role, ToolRoom, UpdateUserAccountInput, User } from '@bechtel/ht-types';
import { Formik, FormikProps } from 'formik';
import { get } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { ErrorRetry } from '~/components';
import { useUpdateUser } from '~/hooks/use-update-user';
import { UserDetailContainerStyled } from './styled';
//@ts-ignore images ignore
import { encodeUrl, i18next as i18n } from '@bechtel/ht-clients-common';
import { useSearchUserDetail } from '~/hooks';
import { ReactComponent as EmptyUser } from '~/images/EmptyState_user-circle.svg';
import { editUserValidationSchema, initialValues, initialValuesProps, UserDetailsProps } from './types';
import { UserInfoDetail } from './user-info-detail';

export const UserDetail: FC<UserDetailsProps> = ({ rolePermissionList }) => {
  const dispatchToast = useToastsDispatch();

  const { userId } = useParams();
  const decodedId = decodeURIComponent(userId ?? '');
  const [formikInitialValues, setFormikInitialValues] = useState<initialValuesProps>(initialValues);
  const { updateUserAccount, loading: updateUserLoading } = useUpdateUser();

  const { searchUserdetail, loading, error, data, networkStatus, refetch } = useSearchUserDetail();

  useEffect(() => {
    if (!decodedId) return;

    searchUserdetail(decodedId, (userDetail: User) => {
      if (userDetail) {
        const data = {
          roles: userDetail.roles ?? [],
          isActive: userDetail.isActive,
          persona: userDetail.employee?.persona ?? '',
          firstName: userDetail.firstName ?? '',
          lastName: userDetail.lastName ?? '',
          bun: userDetail.bun ?? '',
          supervisorBun: userDetail.employee?.supervisor?.user?.bun ?? '',
          supervisorFirstName: userDetail.employee?.supervisor?.firstName ?? '',
          supervisorLastName: userDetail.employee?.supervisor?.lastName ?? '',
          supervisorLobEmployeeId: userDetail.employee?.supervisor?.lobEmployeeId ?? '',
          id: userDetail.id,
          lobEmployeeId: userDetail.employee?.lobEmployeeId ?? '',
          supervisorId: userDetail.employee?.supervisor?.id ?? '',
          upn: userDetail.upn ?? '',
          thUserId: userDetail.employee?.thUserId ?? '',
          toolHoundId: userDetail.employee?.toolHoundId ?? '',
          toolRooms: userDetail.toolRooms ?? [],
          exemptedFromImport: userDetail.employee?.exemptedFromImport,
          thRole: userDetail.employee?.thRole,
        };

        setFormikInitialValues(data);
      }
    });
  }, [decodedId, searchUserdetail]);

  const userDetail: User | null = get(data, 'userById', null);
  //We can enable this if required.As discussed with Sid this should not be mandatory field
  // const updatedSchemaValidation = editUserValidationSchema.shape({
  //   toolRooms: array().when('roles', {
  //     is: (roles) => checkToolRoomsRoles(roles, rolePermissionList),
  //     then: (schema) => schema.min(1, 'Select at least 1 tool room'),
  //     otherwise: (schema) => schema.min(0),
  //   }),
  // });
  if (!decodedId)
    return (
      <UserDetailContainerStyled pl={{ sm: '450' }} pr={{ sm: '500' }}>
        <Box
          flexDirection={{ sm: 'column' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          style={{ height: '100%' }}
        >
          <Box mb={{ sm: '500' }}>
            <EmptyUser />
          </Box>
          <Box pb={{ sm: '300' }} px={{ sm: '600' }}>
            <Text family="openSans" color="grey.700" size="400">
              Select a user to see the details.
            </Text>
          </Box>
          <Box pb={{ sm: '300' }} px={{ sm: '600' }}>
            <Text family="openSans" color="grey.700" size="400">
              You can search for a user by name or unique ID.
            </Text>
          </Box>
        </Box>
      </UserDetailContainerStyled>
    );
  return (
    <Formik
      enableReinitialize={true}
      initialValues={formikInitialValues}
      validationSchema={editUserValidationSchema}
      onSubmit={async (values: initialValuesProps) => {
        const updatedData: UpdateUserAccountInput = {
          roleIds: values?.roles?.map((item: Role) => item.id) ?? [],
          isActive: values.isActive,
          persona: values.persona,
          nameFirst: values.firstName,
          nameLast: values.lastName,
          bun: values.bun,
          id: decodedId,
          lobEmployeeId: values.lobEmployeeId,
          upn: values.upn ? encodeUrl(values.upn) : values.upn,
          toolRoomIds: values?.toolRooms?.map((item: ToolRoom) => item.id) ?? [],
          exemptedFromImport: values.exemptedFromImport,
        };
        if (values.supervisorId) {
          updatedData.supervisorId = values.supervisorId;
        }
        if (values.id) {
          updateUserAccount(
            updatedData,
            (data) => {
              const adhocOrderCreationToast: ToastAction = {
                type: 'ADD_TOAST',
                payload: {
                  toastType: data.error ? ToastType.DANGER : ToastType.SUCCESS,
                  content: data.error ? i18n.t(data.error) : `User updated successfully`,
                  autoDismissTimeout: 3000,
                },
              };
              dispatchToast(adhocOrderCreationToast);
            },
            (error) => {
              const adhocOrderCreationToast: ToastAction = {
                type: 'ADD_TOAST',
                payload: {
                  toastType: ToastType.DANGER,
                  content: i18n.t('COMMON_ERROR_MESSAGE'),
                  autoDismissTimeout: 3000,
                },
              };
              dispatchToast(adhocOrderCreationToast);
            }
          );
        }
      }}
    >
      {(formikProps: FormikProps<initialValuesProps>) => {
        return (
          <UserDetailContainerStyled pl={{ sm: '450' }} pr={{ sm: '500' }}>
            {(networkStatus === NetworkStatus.refetch || loading) && (
              <Box
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                style={{ height: '100%', width: '100%' }}
              >
                <Spinner size={SpinnerSize.LARGE} />
              </Box>
            )}
            {!loading && error && (
              <Box
                alignItems={{ sm: 'center' }}
                justifyContent={{ sm: 'center' }}
                flexGrow={{ sm: 1 }}
                flexDirection={{ sm: 'column' }}
                style={{ height: '100%', width: '100%' }}
              >
                <ErrorRetry refetch={refetch}>
                  There was a problem loading user detail. Please click the button below to try again.
                </ErrorRetry>
              </Box>
            )}
            {!loading && !error && userDetail && (
              <UserInfoDetail
                userDetail={userDetail}
                mutationLoading={updateUserLoading}
                formikProps={formikProps}
                rolePermissionList={rolePermissionList}
              />
            )}
          </UserDetailContainerStyled>
        );
      }}
    </Formik>
  );
};
